import { MAP_TILE_LAYER_ATTRIBUTION, MAP_TILE_LAYER_URL } from '../../../common/utils/map/tileLayer';

export const hasCoordinates = location => Array.isArray(location?.coordinates) && location.coordinates.length === 2;

/**
 * Pin layout used inside the map page (a script string so it runs in the
 * WebView/iframe and can be checked on its own). spreadPinPositions(items,
 * pinHeight, gap) takes pins as { x, y, width } in screen pixels and returns
 * their display centres { x, y }: pins whose boxes would touch are fanned out
 * around their group's middle (side by side for two, a ring for more, spaced
 * so ring neighbours never touch). A group that then touches another group is
 * merged with it and laid out again, until no two pins touch.
 */
export const PIN_SPREAD_SCRIPT = `
function spreadPinPositions(items, pinHeight, gap) {
	var parents = items.map(function (item, index) { return index; });
	var find = function (index) {
		while (parents[index] !== index) {
			parents[index] = parents[parents[index]];
			index = parents[index];
		}
		return index;
	};
	var touches = function (first, firstWidth, second, secondWidth) {
		return Math.abs(first.x - second.x) < (firstWidth + secondWidth) / 2 + gap &&
			Math.abs(first.y - second.y) < pinHeight + gap;
	};
	var mergeTouching = function (positions) {
		var merged = false;
		for (var first = 0; first < items.length; first += 1) {
			for (var second = first + 1; second < items.length; second += 1) {
				if (find(first) !== find(second) && touches(positions[first], items[first].width, positions[second], items[second].width)) {
					parents[find(first)] = find(second);
					merged = true;
				}
			}
		}
		return merged;
	};
	var layout = function () {
		var groups = {};
		items.forEach(function (item, index) {
			var key = find(index);
			groups[key] = (groups[key] || []).concat(index);
		});
		var positions = items.map(function (item) { return { x: item.x, y: item.y }; });
		Object.keys(groups).forEach(function (key) {
			var members = groups[key];
			if (members.length < 2) {
				return;
			}
			var centreX = members.reduce(function (sum, index) { return sum + items[index].x; }, 0) / members.length;
			var centreY = members.reduce(function (sum, index) { return sum + items[index].y; }, 0) / members.length;
			var step = Math.max.apply(null, members.map(function (index) { return items[index].width; }).concat(pinHeight)) + gap;
			var radius = members.length === 2 ? step / 2 : (step * Math.SQRT2) / (2 * Math.sin(Math.PI / members.length));
			members.forEach(function (index, order) {
				var angle = members.length === 2 ? Math.PI * (order + 1) : -Math.PI / 2 + (2 * Math.PI * order) / members.length;
				positions[index] = { x: centreX + Math.cos(angle) * radius, y: centreY + Math.sin(angle) * radius };
			});
		});
		return positions;
	};

	var original = items.map(function (item) { return { x: item.x, y: item.y }; });
	mergeTouching(original);
	var positions = layout();
	// Every merge joins two groups, so this ends after at most items.length rounds.
	while (mergeTouching(positions)) {
		positions = layout();
	}
	return positions;
}
`;

export const buildMapHtml = (points, pins, colors, isDarkMode) => {
	const mapPins = pins
		.filter(pin => pin.coordinates)
		.map(pin => ({ id: pin.id, label: pin.label, status: pin.status, coordinates: pin.coordinates }));
	// The road route follows every point in order, from the start back to the end.
	const routeCoordinates = (points || []).filter(point => hasCoordinates(point.location)).map(point => point.location.coordinates);
	const serializedPins = JSON.stringify(mapPins).replace(/</g, '\\u003c');
	const serializedRoute = JSON.stringify(routeCoordinates);

	return `<!DOCTYPE html>
<html>
	<head>
		<meta charset="utf-8" />
		<meta name="viewport" content="initial-scale=1, maximum-scale=1, user-scalable=no" />
		<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
		<style>
			html, body, #map {
				height: 100%;
				width: 100%;
				margin: 0;
				padding: 0;
				background: ${colors.card};
			}

			.leaflet-container {
				background: ${colors.card};
				font-family: -apple-system, system-ui, sans-serif;
			}

			.leaflet-control-zoom a,
			.leaflet-control-zoom a:hover,
			.leaflet-control-attribution {
				background: ${colors.surface};
				color: ${colors.text};
				border-color: ${colors.border};
			}

			.leaflet-control-attribution {
				font-size: 10px;
				max-width: 60vw;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.leaflet-control-attribution a {
				color: ${colors.accent};
			}

			.route-pin-icon {
				background: transparent;
				border: 0;
			}

			.route-pin {
				height: 44px;
				min-width: 44px;
				padding: 0 10px;
				box-sizing: border-box;
				border: 3px solid ${colors.canvas};
				border-radius: 22px;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 14px;
				font-weight: 600;
				white-space: nowrap;
				cursor: pointer;
				box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
			}

			.route-pin-pending,
			.route-pin-current {
				background: ${colors.primary};
				color: ${colors.onPrimary};
			}

			.route-pin-current {
				border-color: ${colors.currentBorder};
			}

			.route-pin-complete {
				background: ${colors.successBg};
				color: ${colors.onSuccessBg};
			}

			.route-pin-base {
				background: ${colors.chip};
				color: ${colors.onChip};
			}

			.route-pin.selected {
				background: ${colors.text};
				color: ${colors.canvas};
			}

			${isDarkMode ? '.leaflet-tile { -webkit-filter: hue-rotate(180deg) invert(100%); filter: hue-rotate(180deg) invert(100%); }' : ''}
		</style>
	</head>
	<body>
		<div id="map"></div>
		<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
		<script>
			const pins = ${serializedPins};
			const routeCoordinates = ${serializedRoute};
			const routeStyle = { color: '${colors.progress}', weight: 4, opacity: 0.75 };
			const pinElements = {};
			const pinMarkers = [];
			const PIN_HEIGHT = 44;
			const PIN_GAP = 6;

			const post = (message) => {
				const data = JSON.stringify(message);
				if (window.ReactNativeWebView) {
					window.ReactNativeWebView.postMessage(data);
				}
				else if (window.parent && window.parent !== window) {
					window.parent.postMessage(data, '*');
				}
			};

			window.selectPin = (pinId) => {
				Object.keys(pinElements).forEach((id) => {
					const element = pinElements[id];
					if (element) {
						element.classList.toggle('selected', id === pinId);
					}
				});
			};

			// Zoom buttons bottom right: the tapped site card covers the top of the map.
			const map = L.map('map', { zoomControl: false });
			L.control.zoom({ position: 'bottomright' }).addTo(map);
			L.tileLayer('${MAP_TILE_LAYER_URL}', {
				maxZoom: 19,
				attribution: '${MAP_TILE_LAYER_ATTRIBUTION}',
			}).addTo(map);

			map.on('click', () => {
				window.selectPin(null);
				post({ type: 'pin', id: null });
			});

			pins.forEach((pin) => {
				const width = Math.max(44, 26 + pin.label.length * 9);
				const element = document.createElement('div');
				element.className = 'route-pin route-pin-' + pin.status;
				element.setAttribute('role', 'button');
				element.setAttribute('aria-label', 'Stop ' + pin.label);
				element.textContent = pin.label;
				const marker = L.marker(pin.coordinates, {
					icon: L.divIcon({
						className: 'route-pin-icon',
						html: element,
						iconSize: [width, 44],
						iconAnchor: [width / 2, 22],
					}),
					zIndexOffset: pin.status === 'base' ? 0 : 100,
				}).addTo(map);
				pinElements[pin.id] = element;
				pinMarkers.push({ element, width, latLng: L.latLng(pin.coordinates) });
				marker.on('click', (event) => {
					L.DomEvent.stopPropagation(event);
					window.selectPin(pin.id);
					post({ type: 'pin', id: pin.id });
				});
			});

			const fetchRouteSegment = async (startCoordinates, endCoordinates) => {
				const startLonLat = startCoordinates[1] + ',' + startCoordinates[0];
				const endLonLat = endCoordinates[1] + ',' + endCoordinates[0];
				const routeUrl = 'https://router.project-osrm.org/route/v1/driving/' + startLonLat + ';' + endLonLat + '?overview=full&geometries=geojson';

				try {
					const response = await fetch(routeUrl);
					if (!response.ok) {
						throw new Error('Route response was not OK');
					}

					const routeResult = await response.json();
					const routeGeometry = routeResult?.routes?.[0]?.geometry?.coordinates;
					if (!Array.isArray(routeGeometry) || routeGeometry.length < 2) {
						throw new Error('No route geometry');
					}

					return routeGeometry.map(routePoint => [routePoint[1], routePoint[0]]);
				}
				catch (error) {
					return [startCoordinates, endCoordinates];
				}
			};

			const drawRoadRoute = async () => {
				const segmentPromises = [];
				for (let index = 0; index < routeCoordinates.length - 1; index += 1) {
					segmentPromises.push(fetchRouteSegment(routeCoordinates[index], routeCoordinates[index + 1]));
				}

				const segments = await Promise.all(segmentPromises);
				segments.forEach((segment) => {
					if (Array.isArray(segment) && segment.length > 1) {
						L.polyline(segment, routeStyle).addTo(map);
					}
				});
			};

			if (routeCoordinates.length > 1) {
				drawRoadRoute();
			}

			// Pins of nearby sites (e.g. one campus) would sit on top of each other at
			// this zoom: fan each overlapping group out around its middle, with a thin
			// line and dot to the real spot, so every pin stays tappable. Redone on zoom.
			const leaderLayer = L.layerGroup().addTo(map);
			const leaderStyle = { color: '${colors.text}', weight: 2, opacity: 0.6, interactive: false };
			const spotStyle = { radius: 4, color: '${colors.canvas}', weight: 2, fillColor: '${colors.text}', fillOpacity: 1, interactive: false };
			${PIN_SPREAD_SCRIPT}
			const spreadOverlappingPins = () => {
				leaderLayer.clearLayers();
				const points = pinMarkers.map(item => map.latLngToLayerPoint(item.latLng));
				const positions = spreadPinPositions(
					pinMarkers.map((item, index) => ({ x: points[index].x, y: points[index].y, width: item.width })),
					PIN_HEIGHT,
					PIN_GAP,
				);
				pinMarkers.forEach((item, index) => {
					const shiftX = positions[index].x - points[index].x;
					const shiftY = positions[index].y - points[index].y;
					if (!shiftX && !shiftY) {
						item.element.style.transform = '';
						return;
					}

					item.element.style.transform = 'translate(' + shiftX + 'px, ' + shiftY + 'px)';
					L.polyline([item.latLng, map.layerPointToLatLng(L.point(positions[index].x, positions[index].y))], leaderStyle).addTo(leaderLayer);
					L.circleMarker(item.latLng, spotStyle).addTo(leaderLayer);
				});
			};

			if (routeCoordinates.length > 0) {
				// Keep the pins clear of the site card drawn over the top of the map
				// and leave room round the edges for fanned-out pins.
				map.fitBounds(L.latLngBounds(routeCoordinates), { paddingTopLeft: [56, 128], paddingBottomRight: [56, 56] });
			}
			else {
				map.setView([-37.814, 144.96332], 11);
			}
			spreadOverlappingPins();
			map.on('zoomend', spreadOverlappingPins);
		</script>
	</body>
</html>`;
};
